import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Box,
  Paper,
  Grid,
  Skeleton,
  IconButton,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Icon,
  Tooltip,
  Stack,
  TextField,
} from "@mui/material";
import { GridColDef, GridRenderCellParams, DataGrid } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import hubsService from "services/hubsService";
import { toast } from "react-toastify";
import orgDataService from "services/orgDataService";
import MDButton from "components/MDButton";
import Can from "common/Can";

const HubClientList = ({ id, isLoading, clients, getHub }) => {
  const [availableClients, setAvailableClients] = useState([]);
  const [isAddClient, setIsAddClient] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(undefined);
  const [clientHubName, setClientHubName] = useState("");

  useEffect(() => {
    if (!clients) return;
    orgDataService.getAllClients().then((results) => {
      let filteredClients = results.filter((r) => !clients.some((c) => c.id == r.id));
      setAvailableClients(filteredClients);
    });
  }, [clients]);

  const LoadingSkeleton = () => (
    <Box
      sx={{
        height: "fit-content",
        padding: "0px",
        margin: "0px",
        overflow: "hidden",
      }}
    >
      {[...Array(10)].map((_, index) => (
        <Skeleton key={index} variant="rectangular" width="98%" sx={{ my: 3, mx: 1, overflow: "hidden" }} />
      ))}
    </Box>
  );

  const deleteClientRelationship = (clientId) => {
    hubsService.removeClientFromHub(id, clientId).then(() => {
      toast.success("Client removed from Hub");
      setSelectedClientId(null);
      getHub();
    });
  };

  const addClientRelationship = () => {
    if (!selectedClientId) return;

    hubsService.addClientToHub(id, selectedClientId, clientHubName).then(() => {
      toast.success("Client added to Hub");
      setSelectedClientId(null);
      setClientHubName("");
      getHub();
    }).catch(() => {
      toast.error("Error Adding Client to Hub");
    });
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Id",
      flex: 1,
      renderCell: (params: GridRenderCellParams<any, number>) => (
        <Can
          perform={"EditClients"}
          yes={() => <Link to={{ pathname: `/clients/${params.row.id}` }}>{params.value}</Link>}
          no={() => params.value}
        />
      ),
    },
    { field: "name", headerName: "Name", flex: 1 },
    {
      field: "clientHubName",
      headerName: "Client Hub Name",
      flex: 1,
      renderCell: (params) => (params.row.clientHubNames ? params.row.clientHubNames.join(", ") : ""),
    },
    {
      field: "",
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <IconButton title="Delete" onClick={() => deleteClientRelationship(params.row.id)} sx={{ ml: 2 }}>
          <DeleteIcon></DeleteIcon>
        </IconButton>
      ),
    },
  ];

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title={
          <>
            Clients
            <Tooltip title="Add Client" sx={{ ml: 1, verticalAlign: "text-top" }}>
              <Icon
                fontSize="medium"
                onClick={() => {
                  setIsAddClient(!isAddClient);
                  setSelectedClientId(null);
                  setClientHubName("");
                }}
              >
                add_circle_outline
              </Icon>
            </Tooltip>
          </>
        }
      />
      {isAddClient && (
        <Grid item xs={12} sx={{ ml: 3, mb: 2 }}>
          <FormControl sx={{ mr: 2 }}>
            <InputLabel>Clients</InputLabel>
            <Select
              id="clients"
              sx={{ minWidth: 210 }}
              label="Clients"
              value={selectedClientId ?? ""}
              onChange={(e) => setSelectedClientId(+e.target.value!)}
            >
              {availableClients &&
                availableClients.map((c, index) => (
                  <MenuItem key={index} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            label="Client Hub Name"
            value={clientHubName}
            onChange={(e) => setClientHubName(e.target.value)}
            inputProps={{ maxLength: 50 }}
            sx={{ minWidth: 210, mr: 2 }}
          />
          <MDButton
            sx={{ ml: 1 }}
            variant="contained"
            color="dark"
            disabled={!selectedClientId}
            onClick={addClientRelationship}
          >
            Add
          </MDButton>
        </Grid>
      )}
      <CardContent>
        <Grid container spacing={1} sx={{ fontSize: 11 }}>
          <Grid item xs={12}>
            <Box sx={{ mt: 2, overflow: "hidden" }}>
              <Paper sx={{ width: "99%", height: "auto", overflow: "hidden" }}>
                <DataGrid
                  sx={{ overflow: "hidden", height: isLoading ? "100vh" : "auto", fontSize: ".85rem" }}
                  autoHeight={isLoading ? false : true}
                  rows={clients || []}
                  getRowId={(c) => c.hubClientId}
                  columns={columns}
                  disableColumnMenu
                  slots={{
                    loadingOverlay: LoadingSkeleton,
                    noRowsOverlay: () => (
                      <Stack height="100%" alignItems="center" justifyContent="center">
                        No Clients
                      </Stack>
                    ),
                  }}
                  loading={isLoading}
                  density="compact"
                  getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
                />
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
export default HubClientList;
